import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { signedIn } from "../../actions/auth";
import { listActive, listExternal, utcConverter } from "../../actions/banners";
import { sanitizeBannerHtml } from "../../helpers/bannerHtml";

function getHiddenBanners() {
  try {
    return JSON.parse(localStorage.getItem("__banners")) || {};
  } catch {
    return [];
  }
}

function saveHiddenBanners(list) {
  try {
    localStorage.setItem("__banners", JSON.stringify(list));
  } catch {}
}

function addHiddenBanner(id, timestamp) {
  const hidden = getHiddenBanners();
  hidden[id] = timestamp;
  saveHiddenBanners(hidden);
}

function Banner({ object }) {
  const [visible, setVisible] = useState(true);
  const handleClose = () => {
    setVisible(false);
    addHiddenBanner(object.id, object.updated_at);
  };
  if (!visible) {
    return null;
  }
  return (
    <div className={`banner banner-${object.background}`}>
      <button type="button" className="close" onClick={handleClose}>
        <span>×</span>
      </button>
      <div
        // oxlint-disable-next-line react/no-danger -- banner HTML is sanitized with the announcement-only policy
        dangerouslySetInnerHTML={{
          __html: sanitizeBannerHtml(object.html),
        }}
      />
    </div>
  );
}

function withinDisplayWindow(banner) {
  if (!banner.start_time || !banner.end_time) {
    // A banner declares both bounds or neither; one alone is not displayable.
    return !banner.start_time && !banner.end_time;
  }
  const now = new Date();
  const [startHour, startMinute] = utcConverter(true, banner.start_time)
    .split(":")
    .map((n) => parseInt(n, 10));
  const [endHour, endMinute] = utcConverter(true, banner.end_time)
    .split(":")
    .map((n) => parseInt(n, 10));
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), startHour, startMinute);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endHour, endMinute);
  if (end < start) {
    end.setDate(end.getDate() + 1);
  }
  return now.getTime() >= start.getTime() && now.getTime() < end.getTime();
}

export default function Banners() {
  const location = useLocation();
  const pathname = location.pathname;
  const [banners, setBanners] = useState([]);
  useEffect(() => {
    let cancelled = false;
    // Each read carries a generation so a slow earlier request cannot land after
    // a newer one and reinstate a stale audience's banners.
    let generation = 0;
    // `signedIn()` reads the in-memory session that AuthenticatedBoundary fills
    // in only once its /auth/session check resolves. This component renders in
    // the application shell, outside that boundary, so a cold load runs here
    // first and would otherwise keep the signed-out banner set for the life of
    // the page. Re-read on the same user-update seam the shell already uses.
    const load = () => {
      generation += 1;
      const current = generation;
      const stale = () => cancelled || current !== generation;
      const request = signedIn() ? listActive(pathname) : listExternal();
      request
        .then((response) => {
          if (stale()) return;
          const hiddenIds = getHiddenBanners();
          const visible = response.objects.filter((o) => !hiddenIds[o.id] || hiddenIds[o.id] !== o.updated_at);
          setBanners(visible.filter(withinDisplayWindow));
        })
        .catch((error) => {
          // Banners are decoration; a failed read must leave the page usable and
          // must not surface as an unhandled rejection.
          if (stale()) return;
          console.error(error);
          setBanners([]);
        });
    };
    load();
    document.addEventListener("asset:update-user", load);
    return () => {
      cancelled = true;
      document.removeEventListener("asset:update-user", load);
    };
  }, [pathname]);
  if (banners.length === 0) {
    return null;
  }
  return (
    <div className="banners">
      {banners.map((b) => (
        <Banner key={b.id} object={b} />
      ))}
    </div>
  );
}
